import { createPrintFileBatch, IBatchPrintFileDetails, IPrintFileBatchDetails, PrintFileReviewResult } from '../../api/printFileBatches';
import { downloadBatchAssets, finalChoice, initialAssets, putStoredBlob } from './assets';
import { dbDelete, dbDeleteByIndex, dbGet, dbGetAll, dbGetAllByIndex, dbPutMany, dbUpdate } from './db';
import { notifyReviewStore } from './events';
import { addEditLog, currentActor } from './logs';
import { fetchPackDetails } from './packDetails';
import { enqueueBatchStatus, enqueueReview } from './sync';
import { FileVariant, ILocalBatch, ILocalFile, IOutboxOp } from './types';

/** Heavy images of finished, fully saved batches are dropped after this long. */
const PURGE_AFTER_MS = 48 * 60 * 60 * 1000;
/** The server deletes packs that stay "init" for 30 minutes (their files go back to the pool). */
const SERVER_INIT_TTL_MS = 30 * 60 * 1000;
/** Start asking the server a minute early — the local receive time and the server's creation time differ slightly. */
const EXPIRY_CHECK_AFTER_MS = SERVER_INIT_TTL_MS - 60 * 1000;

const withExclusiveLock = async <T,>(name: string, fn: () => Promise<T>): Promise<T> => {
    const locks = (navigator as any).locks;
    if (!locks?.request) return fn();
    return locks.request(name, fn);
};

const requestPersistentStorage = () => {
    try {
        void navigator.storage?.persist?.();
    } catch (e) {
        /* best effort */
    }
};

export const getBatchFiles = async (batchId: string) =>
    (await dbGetAllByIndex<ILocalFile>('files', 'batchId', batchId)).sort((a, b) => a.index - b.index);

/**
 * POST a new designer pack, store it on this device and start downloading its images.
 * Returns null when the server has no eligible files.
 */
export const createBatch = async (opts: { prefetchedFor?: string } = {}): Promise<ILocalBatch | null> =>
    withExclusiveLock('print-file-review-create', async () => {
        const created = await createPrintFileBatch();
        if (!created) {
            if (!opts.prefetchedFor) await addEditLog({ batchId: '-', action: 'batch_none_eligible' });
            return null;
        }
        // Never overwrite a pack this browser already has (its decisions/edits live here).
        const existing = await dbGet<ILocalBatch>('batches', created.printfileBatchId);
        if (existing && !existing.viewOnly) {
            await addEditLog({ batchId: existing.id, action: 'batch_create_returned_existing' });
            return existing;
        }
        const batch: ILocalBatch = {
            id: created.printfileBatchId,
            serverStatus: created.status,
            minOrderDate: created.minOrderDate,
            maxOrderDate: created.maxOrderDate,
            targetSize: created.targetSize,
            fileIds: created.printFiles.map((f) => f.printReadyFileId),
            createdAt: Date.now(),
            createdBy: currentActor(),
            currentIndex: 0,
            prefetchedFor: opts.prefetchedFor,
        };
        const files: ILocalFile[] = created.printFiles.map((f, index) => ({
            ...f,
            id: f.printReadyFileId,
            batchId: batch.id,
            index,
            assets: initialAssets(f),
            decision: null,
            hasManualEdits: false,
            sync: { state: 'idle' },
        }));
        await dbPutMany([
            { store: 'batches', value: batch },
            ...files.map((value) => ({ store: 'files' as const, value })),
        ]);
        await addEditLog({
            batchId: batch.id,
            action: 'batch_created',
            details: {
                files: files.length,
                targetSize: created.targetSize,
                prefetchedFor: opts.prefetchedFor ?? null,
                minOrderDate: created.minOrderDate,
                maxOrderDate: created.maxOrderDate,
            },
        });
        requestPersistentStorage();
        notifyReviewStore();
        void downloadBatchAssets(batch.id);
        return batch;
    });

/** First batch on this device that still has undecided files (oldest first). */
export const findWaitingBatch = async (excludeId?: string) => {
    const batches = await dbGetAll<ILocalBatch>('batches');
    return batches
        .filter((b) => b.id !== excludeId && !b.completedAt && !b.viewOnly && !b.serverDeletedAt)
        .sort((a, b) => a.createdAt - b.createdAt)[0] ?? null;
};

const prefetching = new Set<string>();

/**
 * Halfway through a batch, create the next one in the background so its images are
 * already on this device when the designer finishes. Runs once per batch.
 */
export const maybePrefetchNext = async (batchId: string) => {
    if (prefetching.has(batchId)) return;
    prefetching.add(batchId);
    try {
        const batch = await dbGet<ILocalBatch>('batches', batchId);
        if (!batch || batch.nextRequestedAt || batch.viewOnly) return;
        const files = await getBatchFiles(batchId);
        const decided = files.filter((f) => f.decision).length;
        const reached = Math.max(decided, batch.currentIndex + 1) >= Math.ceil(files.length / 2);
        if (!files.length || !reached) return;
        // A batch already waiting on this device is the "next" one — don't pile up packs.
        const waiting = await findWaitingBatch(batchId);
        const created = waiting ?? (await createBatch({ prefetchedFor: batchId }));
        const next = created && created.id !== batchId ? created : null;
        await dbUpdate<ILocalBatch>('batches', batchId, (b) =>
            b ? { ...b, nextRequestedAt: Date.now(), nextBatchId: next?.id ?? null } : undefined
        );
        await addEditLog({
            batchId,
            action: 'next_batch_prefetch',
            details: { nextBatchId: next?.id ?? null, reused: !!waiting, decided, total: files.length },
        });
        notifyReviewStore();
    } catch (e: any) {
        // Not marked as requested → retried on the next decision.
        await addEditLog({ batchId, action: 'next_batch_prefetch_error', details: { message: e?.message } });
    } finally {
        prefetching.delete(batchId);
    }
};

/** Explicit "get the next batch" at the end of a review (the halfway prefetch found nothing or failed). */
export const createNextBatch = async (fromBatchId: string) => {
    const created = await createBatch({ prefetchedFor: fromBatchId });
    const next = created && created.id !== fromBatchId ? created : null;
    await dbUpdate<ILocalBatch>('batches', fromBatchId, (b) =>
        b ? { ...b, nextRequestedAt: Date.now(), nextBatchId: next?.id ?? null } : undefined
    );
    notifyReviewStore();
    return next;
};

/** Designer opened the batch: remember it and tell the server the review started. */
export const openBatch = async (batchId: string) => {
    // Looking at someone else's pack must not touch its status on the server.
    if ((await dbGet<ILocalBatch>('batches', batchId))?.viewOnly) return;
    let firstOpen = false;
    await dbUpdate<ILocalBatch>('batches', batchId, (b) => {
        if (!b || (b.openedAt && !b.blobsPurgedAt)) return undefined;
        firstOpen = !b.openedAt;
        // Opening re-downloads purged images, so the batch is complete on this device again.
        return { ...b, openedAt: b.openedAt ?? Date.now(), blobsPurgedAt: undefined };
    });
    if (firstOpen) await addEditLog({ batchId, action: 'batch_opened' });
    await enqueueBatchStatus(batchId, 'in_progress');
    void downloadBatchAssets(batchId);
    notifyReviewStore();
};

export const setBatchIndex = async (batchId: string, currentIndex: number) => {
    await dbUpdate<ILocalBatch>('batches', batchId, (b) => (b && b.currentIndex !== currentIndex ? { ...b, currentIndex } : undefined));
    void maybePrefetchNext(batchId);
};

/** Queue the review of a file: what is sent depends on what the chosen variant's final is. */
const enqueueDecision = (file: ILocalFile, decision: PrintFileReviewResult, variant: FileVariant) => {
    switch (finalChoice(file, variant)) {
        case 'edit':
            return enqueueReview(file, decision, true);
        case 'remoteEdit':
            return enqueueReview(file, decision, true, file.sync.finalPrintReadyUrl);
        case 'upscaled':
            // The upscaled draft becomes the final: saved as a manual edit pointing at it.
            return enqueueReview(file, decision, true, file.draftPrintReadyFileUpscaledUrl);
        default:
            return enqueueReview(file, decision, false);
    }
};

/**
 * Record the decision locally and queue it for the server right away.
 * `variant` — the version (regular / upscaled) that was on screen.
 */
export const decideFile = async (file: ILocalFile, decision: PrintFileReviewResult, variant: FileVariant = 'regular') => {
    const res = await dbUpdate<ILocalFile>('files', file.id, (f) => {
        if (!f) return undefined;
        // An edit uploaded from another browser stays the final only while its variant is kept.
        const keepsRemoteEdit = finalChoice(f, variant) === 'remoteEdit';
        return {
            ...f,
            decision,
            decidedAt: Date.now(),
            decidedVariant: variant,
            hasManualEdits: f.edit ? true : keepsRemoteEdit,
        };
    });
    const current = res.value as ILocalFile | null;
    if (!current) return;
    await addEditLog({
        batchId: file.batchId,
        fileId: file.id,
        action: 'decision',
        details: { reviewResult: decision, previous: file.decision, variant, final: finalChoice(current, variant) },
    });
    await enqueueDecision(current, decision, variant);
    const files = await getBatchFiles(file.batchId);
    if (files.every((f) => f.decision)) {
        await dbUpdate<ILocalBatch>('batches', file.batchId, (b) => (b && !b.completedAt ? { ...b, completedAt: Date.now() } : undefined));
        await addEditLog({ batchId: file.batchId, action: 'batch_all_decided' });
    }
    notifyReviewStore();
    void maybePrefetchNext(file.batchId);
};

/** Store the edited print file + master. An already decided file is re-saved on the server. */
export const saveFileEdit = async (
    file: ILocalFile,
    result: { finalBlob: Blob; masterBlob: Blob | null; width: number; height: number },
    details: Record<string, unknown>,
    variant: FileVariant = 'regular'
) => {
    await putStoredBlob(file, 'final', result.finalBlob);
    if (result.masterBlob) await putStoredBlob(file, 'editedMaster', result.masterBlob);
    else await dbDelete('blobs', `${file.id}:editedMaster`);
    const res = await dbUpdate<ILocalFile>('files', file.id, (f) =>
        f
            ? {
                  ...f,
                  hasManualEdits: true,
                  // A decided file now stands for the edit made from this variant.
                  decidedVariant: f.decision ? variant : f.decidedVariant,
                  edit: {
                      savedAt: Date.now(),
                      width: result.width,
                      height: result.height,
                      type: result.finalBlob.type,
                      bytes: result.finalBlob.size,
                      variant,
                  },
              }
            : undefined
    );
    await addEditLog({ batchId: file.batchId, fileId: file.id, action: 'edit_save', details: { ...details, variant } });
    const current = res.value as ILocalFile | null;
    if (current?.decision) await enqueueDecision(current, current.decision, variant);
    notifyReviewStore();
};

/** Drop the manual edit (made here or uploaded from another browser) and go back to the server draft. */
export const revertFileEdit = async (file: ILocalFile) => {
    await dbDelete('blobs', `${file.id}:final`);
    await dbDelete('blobs', `${file.id}:editedMaster`);
    const res = await dbUpdate<ILocalFile>('files', file.id, (f) => (f ? { ...f, hasManualEdits: false, edit: undefined } : undefined));
    await addEditLog({ batchId: file.batchId, fileId: file.id, action: 'edit_revert' });
    const current = res.value as ILocalFile | null;
    if (current?.decision) await enqueueDecision(current, current.decision, current.decidedVariant ?? 'regular');
    notifyReviewStore();
};

/**
 * Free disk space: drop the downloaded images of a batch. Edited files that are not yet
 * on the server are kept. Decisions and logs stay.
 */
export const purgeBatchImages = async (batchId: string) => {
    const outbox = await dbGetAll<IOutboxOp>('outbox');
    const pendingUploads = new Set(outbox.filter((o) => o.kind === 'review' && o.batchId === batchId).map((o) => (o as any).fileId));
    const removed = await dbDeleteByIndex('blobs', 'batchId', batchId, (rec) => rec.kind === 'final' && pendingUploads.has(rec.fileId));
    const files = await getBatchFiles(batchId);
    await dbPutMany(
        files.map((f) => ({
            store: 'files' as const,
            value: {
                ...f,
                assets: Object.fromEntries(
                    Object.entries(f.assets).map(([k, a]) => [k, a.state === 'missing' ? a : { ...a, state: 'pending' }])
                ),
                finalAsset: f.finalAsset ? { ...f.finalAsset, state: 'pending' } : undefined,
            },
        }))
    );
    await dbUpdate<ILocalBatch>('batches', batchId, (b) => (b ? { ...b, blobsPurgedAt: Date.now() } : undefined));
    await addEditLog({ batchId, action: 'images_purged', details: { removed } });
    notifyReviewStore();
};

/** Purge images of batches finished and saved on the server more than 48h ago. */
export const autoPurgeOldBatches = async () => {
    const batches = await dbGetAll<ILocalBatch>('batches');
    const cutoff = Date.now() - PURGE_AFTER_MS;
    for (const b of batches) {
        if (b.viewOnly) {
            if ((b.fetchedAt ?? b.createdAt) < cutoff) await removeViewOnlyPack(b.id);
            continue;
        }
        const since = Math.max(b.doneSyncedAt ?? 0, b.blobsRestoredAt ?? 0);
        if (b.doneSyncedAt && since < cutoff && !b.blobsPurgedAt) await purgeBatchImages(b.id);
    }
};

/** Delete a pack from this browser entirely: records, images, pending requests and its log. */
export const removeLocalPack = async (batchId: string) => {
    await dbDeleteByIndex('blobs', 'batchId', batchId);
    await dbDeleteByIndex('files', 'batchId', batchId);
    await dbDeleteByIndex('logs', 'batchId', batchId);
    const ops = (await dbGetAll<IOutboxOp>('outbox')).filter((o) => o.batchId === batchId);
    for (const op of ops) await dbDelete('outbox', op.key);
    await dbDelete('batches', batchId);
    // A pack that counted on this one as its "next" requests a new one on the next decision.
    const linked = (await dbGetAll<ILocalBatch>('batches')).filter((b) => b.nextBatchId === batchId);
    for (const b of linked) {
        await dbUpdate<ILocalBatch>('batches', b.id, (cur) => (cur ? { ...cur, nextBatchId: undefined, nextRequestedAt: undefined } : undefined));
    }
    notifyReviewStore();
};

/** Drop a pack that was only loaded for viewing. */
export const removeViewOnlyPack = async (batchId: string) => {
    const batch = await dbGet<ILocalBatch>('batches', batchId);
    if (batch?.viewOnly) await removeLocalPack(batchId);
};

/**
 * The server deletes packs left in "init" for 30 minutes. Ask about every local pack that is
 * still "init" and old enough: 404 → remove it from this browser (or, if designer work would
 * be lost, flag it for a manual remove); a newer status → take it over.
 * Resolves with the ids removed.
 */
export const pruneExpiredPacks = async (): Promise<string[]> => {
    const now = Date.now();
    const due = (await dbGetAll<ILocalBatch>('batches')).filter(
        (b) => b.serverStatus === 'init'
            && !b.serverDeletedAt
            && now - (b.viewOnly ? b.fetchedAt ?? b.createdAt : b.createdAt) >= EXPIRY_CHECK_AFTER_MS
    );
    const removed: string[] = [];
    for (const b of due) {
        try {
            const details = await fetchPackDetails(b.id, 0);
            if (details.status !== b.serverStatus) {
                await dbUpdate<ILocalBatch>('batches', b.id, (cur) => (cur ? { ...cur, serverStatus: details.status } : undefined));
            }
        } catch (e: any) {
            if (e?.status !== 404) continue; // offline / server trouble — ask again next time
            const files = await getBatchFiles(b.id);
            const outbox = await dbGetAll<IOutboxOp>('outbox');
            const hasWork = !b.viewOnly && (
                files.some((f) => f.decision || f.hasManualEdits)
                || outbox.some((o) => o.batchId === b.id && o.kind === 'review')
            );
            if (hasWork) {
                await dbUpdate<ILocalBatch>('batches', b.id, (cur) => (cur ? { ...cur, serverDeletedAt: Date.now() } : undefined));
                await addEditLog({ batchId: b.id, action: 'pack_deleted_on_server', details: { keptLocalWork: true } });
            } else {
                await removeLocalPack(b.id);
                removed.push(b.id);
            }
        }
    }
    notifyReviewStore();
    return removed;
};

/** A server file as a local record (only what the review page uses — the history stays in the details cache). */
const fromServerFile = (f: IBatchPrintFileDetails, batchId: string, index: number, prev?: ILocalFile): ILocalFile => {
    // Keep images already here when their URLs didn't change.
    const sameUrls = !!prev
        && prev.draftPrintReadyUrl === f.draftPrintReadyUrl
        && prev.backgroundRemovedUrl === f.backgroundRemovedUrl
        && prev.originalUrl === f.originalUrl
        && prev.mockupUrl === f.mockupUrl
        && (prev.draftPrintReadyFileUpscaledUrl ?? null) === (f.draftPrintReadyFileUpscaledUrl ?? null)
        && (prev.backgroundRemovedFileUpscaledUrl ?? null) === (f.backgroundRemovedFileUpscaledUrl ?? null);
    // An approved upscaled draft is stored by the server as a "manual edit" pointing at it.
    const upscaledChosen = !!f.reviewResult
        && !!f.hasManualEdits
        && !!f.draftPrintReadyFileUpscaledUrl
        && f.finalPrintReadyUrl === f.draftPrintReadyFileUpscaledUrl;
    return {
        printReadyFileId: f.printReadyFileId,
        orderNumber: f.orderNumber,
        sku: f.sku,
        originalUrl: f.originalUrl,
        mockupUrl: f.mockupUrl,
        croppingNotes: f.croppingNotes,
        color: f.color,
        printFileStatus: f.printFileStatus,
        draftPrintReadyUrl: f.draftPrintReadyUrl,
        backgroundRemovedUrl: f.backgroundRemovedUrl,
        draftPrintReadyFileUpscaledUrl: f.draftPrintReadyFileUpscaledUrl ?? null,
        backgroundRemovedFileUpscaledUrl: f.backgroundRemovedFileUpscaledUrl ?? null,
        id: f.printReadyFileId,
        batchId,
        index,
        assets: sameUrls && prev ? { ...initialAssets(f), ...prev.assets } : initialAssets(f),
        finalAsset: prev?.finalAsset,
        decision: f.reviewResult ?? null,
        decidedVariant: f.reviewResult ? (upscaledChosen ? 'upscaled' : 'regular') : undefined,
        hasManualEdits: !!f.hasManualEdits && !upscaledChosen,
        sync: { state: f.reviewResult ? 'synced' : 'idle', finalPrintReadyUrl: f.finalPrintReadyUrl ?? null },
    };
};

const storeServerPack = async (details: IPrintFileBatchDetails, batch: ILocalBatch, action: string) => {
    const previous = new Map((await getBatchFiles(batch.id)).map((f) => [f.id, f]));
    const files = details.printFiles.map((f, index) => fromServerFile(f, batch.id, index, previous.get(f.printReadyFileId)));
    await dbPutMany([
        { store: 'batches', value: batch },
        ...files.map((value) => ({ store: 'files' as const, value })),
    ]);
    await addEditLog({ batchId: batch.id, action, details: { files: files.length, status: batch.serverStatus } });
    notifyReviewStore();
    void downloadBatchAssets(batch.id);
    return files;
};

/**
 * Load someone else's set just to look at it: files, decisions and final URLs from the
 * server, images into this browser. Never editable here.
 */
export const loadPackForViewing = async (batchId: string): Promise<ILocalBatch> => {
    const existing = await dbGet<ILocalBatch>('batches', batchId);
    if (existing && !existing.viewOnly) return existing;
    const details = await fetchPackDetails(batchId, 0);
    const batch: ILocalBatch = {
        id: details.printfileBatchId,
        serverStatus: details.status,
        minOrderDate: details.minOrderDate ?? null,
        maxOrderDate: details.maxOrderDate ?? null,
        targetSize: details.fileCount,
        fileIds: details.printFiles.map((f) => f.printReadyFileId),
        createdAt: existing?.createdAt ?? Date.now(),
        createdBy: details.createdBy ?? null,
        currentIndex: 0,
        viewOnly: true,
        fetchedAt: Date.now(),
        serverUpdatedAt: details.updatedAt,
    };
    await storeServerPack(details, batch, 'pack_loaded_for_viewing');
    return batch;
};

/**
 * Bring the designer's OWN set into this browser to keep reviewing it (it was taken in
 * another browser): files and the decisions already saved come from the server, the review
 * continues from the first undecided file. A set that is already here is synced instead.
 */
export const loadOwnPack = async (batchId: string): Promise<ILocalBatch | null> => {
    const existing = await dbGet<ILocalBatch>('batches', batchId);
    if (existing && !existing.viewOnly) {
        await syncOwnPackFromServer(batchId);
        return (await dbGet<ILocalBatch>('batches', batchId)) ?? null;
    }
    const details = await fetchPackDetails(batchId, 0);
    const now = Date.now();
    const firstUndecided = details.printFiles.findIndex((f) => !f.reviewResult);
    const batch: ILocalBatch = {
        id: details.printfileBatchId,
        serverStatus: details.status,
        minOrderDate: details.minOrderDate ?? null,
        maxOrderDate: details.maxOrderDate ?? null,
        targetSize: details.fileCount,
        fileIds: details.printFiles.map((f) => f.printReadyFileId),
        createdAt: now,
        createdBy: details.createdBy ?? currentActor(),
        currentIndex: Math.max(0, firstUndecided),
        openedAt: details.status === 'init' ? undefined : now,
        completedAt: firstUndecided === -1 && details.printFiles.length ? now : undefined,
        doneSyncedAt: details.status === 'done' ? now : undefined,
        fetchedAt: now,
        serverUpdatedAt: details.updatedAt,
    };
    await storeServerPack(details, batch, 'pack_loaded_from_server');
    requestPersistentStorage();
    return batch;
};

/**
 * Pull what another browser saved for the designer's own set: reviews the server has and
 * this browser doesn't. A decision still waiting in the outbox here is newer and is kept.
 * Resolves with the number of files updated.
 */
export const syncOwnPackFromServer = async (batchId: string): Promise<number> => {
    const batch = await dbGet<ILocalBatch>('batches', batchId);
    if (!batch || batch.viewOnly || batch.serverDeletedAt) return 0;
    // Anything decided or saved here after this moment is newer than the answer we are about to get.
    const startedAt = Date.now();
    const details = await fetchPackDetails(batchId, 0);
    const outbox = (await dbGetAll<IOutboxOp>('outbox')).filter((o) => o.batchId === batchId);
    const pendingFiles = new Set(outbox.filter((o) => o.kind === 'review').map((o) => (o as { fileId: string }).fileId));
    const locals = new Map((await getBatchFiles(batchId)).map((f) => [f.id, f]));
    let changed = 0;
    for (let index = 0; index < details.printFiles.length; index++) {
        const sf = details.printFiles[index];
        const lf = locals.get(sf.printReadyFileId);
        if (!lf || !sf.reviewResult || pendingFiles.has(lf.id)) continue;
        if ((lf.decidedAt ?? 0) >= startedAt || (lf.sync.syncedAt ?? 0) >= startedAt) continue;
        const server = fromServerFile(sf, batchId, index, lf);
        const upToDate = lf.sync.state === 'synced'
            && lf.decision === server.decision
            && (lf.sync.finalPrintReadyUrl ?? null) === (server.sync.finalPrintReadyUrl ?? null);
        if (upToDate) continue;
        // A local edit stays only if it is what the server has as the final.
        const keepLocalEdit = !!lf.edit && lf.sync.state === 'synced' && lf.sync.finalPrintReadyUrl === server.sync.finalPrintReadyUrl;
        if (lf.edit && !keepLocalEdit) {
            await dbDelete('blobs', `${lf.id}:final`);
            await dbDelete('blobs', `${lf.id}:editedMaster`);
        }
        await dbUpdate<ILocalFile>('files', lf.id, (cur) =>
            cur
                ? {
                      ...cur,
                      printFileStatus: server.printFileStatus,
                      decision: server.decision,
                      decidedAt: cur.decidedAt ?? Date.now(),
                      decidedVariant: keepLocalEdit ? cur.decidedVariant : server.decidedVariant,
                      hasManualEdits: keepLocalEdit ? true : server.hasManualEdits,
                      edit: keepLocalEdit ? cur.edit : undefined,
                      sync: { state: 'synced', finalPrintReadyUrl: server.sync.finalPrintReadyUrl, syncedAt: Date.now() },
                  }
                : undefined
        );
        changed += 1;
    }
    const statusPending = outbox.some((o) => o.kind === 'batchStatus');
    const files = await getBatchFiles(batchId);
    const allDecided = files.length > 0 && files.every((f) => f.decision);
    await dbUpdate<ILocalBatch>('batches', batchId, (b) =>
        b
            ? {
                  ...b,
                  serverStatus: statusPending ? b.serverStatus : details.status,
                  serverUpdatedAt: details.updatedAt,
                  fetchedAt: Date.now(),
                  completedAt: b.completedAt ?? (allDecided ? Date.now() : undefined),
                  doneSyncedAt: b.doneSyncedAt ?? (details.status === 'done' && !statusPending ? Date.now() : undefined),
              }
            : undefined
    );
    if (changed) {
        await addEditLog({ batchId, action: 'pack_synced_from_server', details: { files: changed } });
        void downloadBatchAssets(batchId);
    }
    notifyReviewStore();
    return changed;
};

/** On page open: pull what other browsers saved for every own set that isn't finished here. */
export const syncOwnPacksFromServer = async () => {
    const batches = await dbGetAll<ILocalBatch>('batches');
    for (const b of batches) {
        if (b.viewOnly || b.serverDeletedAt || b.doneSyncedAt) continue;
        try {
            await syncOwnPackFromServer(b.id);
        } catch (e) {
            /* offline, or deleted on the server (pruneExpiredPacks deals with that) */
        }
    }
};

/** Download the images of a purged pack again so it can be viewed. */
export const redownloadPack = async (batchId: string) => {
    await dbUpdate<ILocalBatch>('batches', batchId, (b) =>
        b ? { ...b, blobsPurgedAt: undefined, blobsRestoredAt: Date.now() } : undefined
    );
    await addEditLog({ batchId, action: 'images_redownload' });
    notifyReviewStore();
    await downloadBatchAssets(batchId);
};

/** Resume unfinished downloads (after a reload; done packs also fetch their server finals). */
export const resumeDownloads = async () => {
    const batches = await dbGetAll<ILocalBatch>('batches');
    batches.filter((b) => !b.blobsPurgedAt).forEach((b) => void downloadBatchAssets(b.id));
};
